/* eslint-disable react/no-array-index-key */
import {Typography} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useIntl} from 'react-intl';

import {LightIconButton, LightIconButtonProps} from './LightIconButton';
import {LightLoadingSpinner} from './LightLoadingSpinner';
import {LightSplitLayout, LightSplitLayoutProps} from './LightSplitLayout';
import {NotFoundComponent} from './NotFoundComponent';
import {TranslateUtils} from '../common';
import {useLocalStorage} from '../hooks';

export type NewMinimizedMaximized = {
  minimized?: boolean;
  expanded?: boolean;
  maximized?: boolean;
};
export type LightLayoutNodeFCProps = {
  minimized: boolean;
  expanded: boolean;
  maximized: boolean;
  setMinimizedMaximized: (newValues: NewMinimizedMaximized) => void;
};
type ValuesOrCallback<T> = T[] | ((minimized: boolean[]) => T[]);
export type LightLayoutNodeType<T extends LightLayoutNodeFCProps = any> = Omit<LightSplitLayoutProps, 'storageId' | 'minSizes'> & {
  minSizes: ValuesOrCallback<number>;
  nodes: (LightLayoutNodeType<T> | React.FC<T>)[];
  nodeProps?: T[];
  nodeKeys?: (string | null)[];
};
type LightPageLayoutState = {
  minimized: Record<string, boolean[]>;
  maximized: string;
};
type LightLayoutNodeProps<T extends LightLayoutNodeFCProps> = LightLayoutNodeType<T> & {
  storageId: string;
  state: LightPageLayoutState;
  setState: (newValue: LightPageLayoutState) => void;
};
function warnIfContainsDashes(storageKey: string) {
  if (storageKey.includes('-')) {
    console.error(`LightLayoutNode storageId or nodeKey cannot contain "-", "${storageKey}"`);
  }
}
function LightLayoutNode<T extends LightLayoutNodeFCProps>(props: LightLayoutNodeProps<T>) {
  const {nodes, state, setState, storageId, minSizes, noGrow, attributes, nodeProps = [], nodeKeys, gutterSize, ...splitProps} = props;
  const minimized = useMemo(() => {
    const acc = Array(nodes.length).fill(false);
    (state.minimized[storageId] ?? []).forEach((v, i) => {
      if (v) acc[i] = true;
    });
    return acc;
  }, [nodes.length, state.minimized, storageId]);
  const {maximized} = state;
  const nodeInfos = useMemo(
    () =>
      nodes.map((node, i) => {
        const nodeAttributes = attributes?.[i] ?? {};
        const isPageBox = nodeAttributes['data-page-box'] ?? node instanceof Function;
        const isBoxMinimized = minimized[i];
        const boxKey = String(nodeKeys?.[i] || i);
        warnIfContainsDashes(boxKey);
        const nodeIsVertical = node instanceof Function ? false : node.isVertical;
        const boxStorageId = `${storageId}-${boxKey}${nodeIsVertical ? 'v' : 'h'}`;
        const isBoxMaximized = maximized.startsWith(boxStorageId);
        return {
          nodeAttributes,
          isPageBox,
          boxKey,
          boxStorageId,
          isBoxMinimized,
          isBoxMaximized,
        };
      }),
    [attributes, nodeKeys, storageId, nodes, minimized, maximized]
  );
  const realMinSizes = useMemo(() => {
    const acc = minSizes instanceof Function ? minSizes(minimized) : [...minSizes];
    if (maximized) {
      nodes.forEach((_, i) => {
        if (!maximized.startsWith(nodeInfos[i].boxStorageId)) acc[i] = 0;
      });
    }
    return acc;
  }, [minimized, minSizes, nodes, maximized, nodeInfos]);
  const realNoGrow = useMemo(() => {
    const acc = Array(nodes.length).fill(false);
    noGrow?.forEach((v, i) => {
      acc[i] = v;
    });
    minimized.forEach((v, i) => {
      if (v) acc[i] = true;
    });
    if (maximized) {
      nodes.forEach((_, i) => {
        acc[i] = !maximized.startsWith(nodeInfos[i].boxStorageId);
      });
    }
    return acc;
  }, [nodes, noGrow, minimized, maximized, nodeInfos]);
  const realAttributes = useMemo(() => {
    const acc = Array(nodes.length)
      .fill(0)
      .map((_, i) => {
        const {nodeAttributes, isBoxMinimized, isBoxMaximized, isPageBox} = nodeInfos[i];
        return {
          ...nodeAttributes,
          'data-minimized': isBoxMinimized,
          'data-maximized': maximized && isBoxMaximized,
          'data-page-box': isPageBox,
        };
      });
    return acc;
  }, [nodes, nodeInfos, maximized]);
  const minimizedCount = minimized.reduce((acc, v) => acc + +v, 0);
  return (
    <LightSplitLayout
      storageId={storageId}
      minSizes={realMinSizes}
      noGrow={realNoGrow}
      attributes={realAttributes}
      {...splitProps}
      gutterSize={maximized ? 0 : gutterSize}
    >
      {nodes.map((ComponentOrNode, i) => {
        const {boxKey, boxStorageId, isBoxMaximized} = nodeInfos[i];
        return ComponentOrNode instanceof Function ? (
          <ComponentOrNode
            {...(nodeProps[i] ?? {})}
            key={boxKey}
            minimized={minimized[i] && !maximized}
            expanded={minimizedCount === minimized.length - 1 && !minimized[i]}
            maximized={Boolean(maximized && isBoxMaximized)}
            setMinimizedMaximized={(newValues: NewMinimizedMaximized) => {
              const {minimized: wantMinimized, expanded: wantExpanded, maximized: wantMaximized} = newValues;
              const accNewState = {...state};
              if (wantMinimized != null) {
                const accNewMinimized = [...minimized];
                accNewMinimized[i] = wantMinimized;
                accNewState.minimized = {
                  ...state.minimized,
                  [storageId]: accNewMinimized,
                };
              }
              if (wantExpanded != null) {
                let accNewMinimized: boolean[] = [];
                if (wantExpanded) {
                  accNewMinimized = minimized.map(() => true);
                  accNewMinimized[i] = false;
                } else {
                  accNewMinimized = minimized.map(() => false);
                }
                accNewState.minimized = {
                  ...state.minimized,
                  [storageId]: accNewMinimized,
                };
              }
              if (wantMaximized != null) {
                accNewState.maximized = wantMaximized ? boxStorageId : '';
              }
              setState(accNewState);
            }}
          />
        ) : (
          <LightLayoutNode
            key={boxKey}
            state={state}
            setState={setState}
            storageId={boxStorageId}
            {...(ComponentOrNode as LightLayoutNodeType<T>)}
          />
        );
      })}
    </LightSplitLayout>
  );
}
type Props = {
  loading: boolean;
  havePermissions?: boolean;
  isError?: boolean;
  storageId: string;
  layout?: LightLayoutNodeType<any>;
};
export const LightPageLayout: React.FC<Props> = (props) => {
  const {loading, havePermissions = null, isError, storageId, layout} = props;
  const intl = useIntl();
  const [initialLoading, setInitialLoading] = useState(true); // NOTE: always show page as loading at first, to switch pages instantly when user clicks a link
  useEffect(() => {
    const timeout = window.setTimeout(() => setInitialLoading(false), 250);
    return () => {
      window.clearTimeout(timeout);
    };
  }, []);
  const [state, _, setState] = useLocalStorage(`${storageId}.layout`, {
    minimized: {},
    maximized: '',
  } as LightPageLayoutState);
  const content = useMemo(() => {
    if (loading || initialLoading) return <LightLoadingSpinner />;
    if (havePermissions !== null && !havePermissions)
      return <Typography variant='h1'>{TranslateUtils.translate('permissions.insuficientrights', intl)}</Typography>;
    if (isError) return <LightNotFoundComponentBox />;
    if (!layout) return <></>;
    return <LightLayoutNode state={state} setState={setState} storageId={storageId} {...layout} />;
  }, [loading, initialLoading, havePermissions, isError, state, setState, storageId, layout, intl]);
  // if maximized node is not currently accessible, reset maximized
  warnIfContainsDashes(storageId);
  useEffect(() => {
    let curr = layout;
    state.maximized
      .split('-')
      .slice(1)
      .forEach((fullKey) => {
        const key = fullKey.slice(0, -1);
        const children = (curr?.nodes ?? []) as LightLayoutNodeType<any>[];
        curr = children.find((_, i) => {
          const childKey = String(curr?.nodeKeys?.[i] ?? i);
          return childKey === key;
        });
      });
    if (curr == null) {
      setState({
        ...state,
        maximized: '',
      });
    }
  }, [layout, state, setState]);
  return (
    <div data-name='content-box-wrapper'>
      <div data-name='content-box' data-loading={loading || initialLoading} data-some-maximized={state.maximized !== ''}>
        {content}
      </div>
    </div>
  );
};
const LightNotFoundComponentBox: React.FC = (_props) => {
  const intl = useIntl();
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 16,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'white',
      }}
    >
      <Helmet title={TranslateUtils.translate('notFound.pageTitle', intl)} />
      <NotFoundComponent />
    </div>
  );
};

type FlipButtonTranslations = {
  unselected: string;
  selected: string;
};
const MINIMIZE_TRANSLATIONS: FlipButtonTranslations = {
  unselected: 'global.minimize',
  selected: 'global.unminimize',
};
const EXPAND_TRANSLATIONS: FlipButtonTranslations = {
  unselected: 'common.collapseButton.expand',
  selected: 'common.collapseButton.collapse',
};
const MAXIMIZE_TRANSLATIONS: FlipButtonTranslations = {
  unselected: 'global.maximize',
  selected: 'global.unmaximize',
};
type FlipButtonProps = {
  selected: boolean;
  translations: FlipButtonTranslations;
  flip: boolean;
  toggleFlip: () => void;
  wrapperStyle?: React.CSSProperties;
  style?: React.CSSProperties;
};

const MINIMIZE_OR_MAXIMIZE_BUTTON_STYLE = {
  borderRadius: 8,
  border: 'none',
  background: 'var(--dlThemeGray-50)',
  color: 'var(--dlMultiColorSecondary)',
};
type MinimizeButtonProps = {
  minimized: boolean;
  toggleMinimized: () => void;
  wrapperStyle?: React.CSSProperties;
  style?: React.CSSProperties;
};
type ExpandButtonProps = {
  expanded: boolean;
  toggleExpanded: () => void;
  wrapperStyle?: React.CSSProperties;
  style?: React.CSSProperties;
};

// minimize/expand horizontal
const MinimizeHorizontalButton: React.FC<FlipButtonProps> = (props) => {
  const {selected, translations, flip, toggleFlip, wrapperStyle, style} = props;
  const intl = useIntl();
  const t = useCallback((key: string) => TranslateUtils.translate(key, intl), [intl]);
  return (
    <LightIconButton
      title={selected ? t(translations.selected) : t(translations.unselected)}
      iconName='arrowLeftIcon'
      useFixedViewBox='0 0 16 24'
      wrapperStyle={{
        ...MINIMIZE_OR_MAXIMIZE_BUTTON_STYLE,
        width: 32,
        height: 48,
        transform: flip ? 'rotate(180deg)' : undefined,
        ...(wrapperStyle ?? {}),
      }}
      style={{width: 16, height: 24, ...(style ?? {})}}
      onClick={toggleFlip}
      selected={selected}
    />
  );
};
export const MinimizeLeftButton: React.FC<MinimizeButtonProps> = (props) => {
  const {minimized, toggleMinimized, wrapperStyle, style} = props;
  return (
    <MinimizeHorizontalButton
      selected={minimized}
      translations={MINIMIZE_TRANSLATIONS}
      flip={minimized}
      toggleFlip={toggleMinimized}
      wrapperStyle={wrapperStyle}
      style={style}
    />
  );
};
export const MinimizeRightButton: React.FC<MinimizeButtonProps> = (props) => {
  const {minimized, toggleMinimized, wrapperStyle, style} = props;
  return (
    <MinimizeHorizontalButton
      selected={minimized}
      translations={MINIMIZE_TRANSLATIONS}
      flip={!minimized}
      toggleFlip={toggleMinimized}
      wrapperStyle={wrapperStyle}
      style={style}
    />
  );
};
export const ExpandLeftButton: React.FC<ExpandButtonProps> = (props) => {
  const {expanded, toggleExpanded, wrapperStyle, style} = props;
  return (
    <MinimizeHorizontalButton
      selected={expanded}
      translations={EXPAND_TRANSLATIONS}
      flip={expanded}
      toggleFlip={toggleExpanded}
      wrapperStyle={wrapperStyle}
      style={style}
    />
  );
};
export const ExpandRightButton: React.FC<ExpandButtonProps> = (props) => {
  const {expanded, toggleExpanded, wrapperStyle, style} = props;
  return (
    <MinimizeHorizontalButton
      selected={expanded}
      translations={EXPAND_TRANSLATIONS}
      flip={!expanded}
      toggleFlip={toggleExpanded}
      wrapperStyle={wrapperStyle}
      style={style}
    />
  );
};

// minimize/expand vertical
const MinimizeVerticalButton: React.FC<FlipButtonProps> = (props) => {
  const {selected, translations, flip, toggleFlip, wrapperStyle, style} = props;
  const intl = useIntl();
  const t = useCallback((key: string) => TranslateUtils.translate(key, intl), [intl]);
  return (
    <LightIconButton
      title={selected ? t(translations.selected) : t(translations.unselected)}
      iconName='arrowDownIcon'
      useFixedViewBox='0 0 24 16'
      wrapperStyle={{
        ...MINIMIZE_OR_MAXIMIZE_BUTTON_STYLE,
        width: 32,
        height: 32,
        transform: flip ? 'rotate(180deg)' : undefined,
        ...(wrapperStyle ?? {}),
      }}
      style={{width: 24, height: 16, ...(style ?? {})}}
      onClick={toggleFlip}
      selected={selected}
    />
  );
};
export const MinimizeUpButton: React.FC<MinimizeButtonProps> = (props) => {
  const {minimized, toggleMinimized, wrapperStyle, style} = props;
  return (
    <MinimizeVerticalButton
      selected={minimized}
      translations={MINIMIZE_TRANSLATIONS}
      flip={!minimized}
      toggleFlip={toggleMinimized}
      wrapperStyle={wrapperStyle}
      style={style}
    />
  );
};
export const MinimizeDownButton: React.FC<MinimizeButtonProps> = (props) => {
  const {minimized, toggleMinimized, wrapperStyle, style} = props;
  return (
    <MinimizeVerticalButton
      selected={minimized}
      translations={MINIMIZE_TRANSLATIONS}
      flip={minimized}
      toggleFlip={toggleMinimized}
      wrapperStyle={wrapperStyle}
      style={style}
    />
  );
};
export const ExpandUpButton: React.FC<ExpandButtonProps> = (props) => {
  const {expanded, toggleExpanded, wrapperStyle, style} = props;
  return (
    <MinimizeVerticalButton
      selected={expanded}
      translations={EXPAND_TRANSLATIONS}
      flip={!expanded}
      toggleFlip={toggleExpanded}
      wrapperStyle={wrapperStyle}
      style={style}
    />
  );
};
export const ExpandDownButton: React.FC<ExpandButtonProps> = (props) => {
  const {expanded, toggleExpanded, wrapperStyle, style} = props;
  return (
    <MinimizeVerticalButton
      selected={expanded}
      translations={EXPAND_TRANSLATIONS}
      flip={expanded}
      toggleFlip={toggleExpanded}
      wrapperStyle={wrapperStyle}
      style={style}
    />
  );
};

// maximize
type MaximizeButtonProps = {
  maximized: boolean;
  toggleMaximized: () => void;
  tall?: boolean;
  style?: React.CSSProperties;
  colors?: LightIconButtonProps['colors'];
  disabled?: boolean;
  toGrayscaleOnDisabled?: boolean;
};
export const MaximizeButton: React.FC<MaximizeButtonProps> = (props) => {
  const {maximized, toggleMaximized, tall, style, colors, disabled, toGrayscaleOnDisabled = false} = props;
  const intl = useIntl();
  const t = useCallback((key: string) => TranslateUtils.translate(key, intl), [intl]);
  return (
    <LightIconButton
      title={maximized ? t(MAXIMIZE_TRANSLATIONS.selected) : t(MAXIMIZE_TRANSLATIONS.unselected)}
      iconName='maximizeIcon'
      useFixedViewBox='0 0 24 24'
      wrapperStyle={{
        ...MINIMIZE_OR_MAXIMIZE_BUTTON_STYLE,
        width: 32,
        height: tall ? 48 : 32,
        transform: maximized ? 'rotate(180deg)' : undefined,
        filter: disabled && toGrayscaleOnDisabled ? 'grayscale(100%)' : 'none',
      }}
      style={{width: 24, height: 24, ...(style ?? {})}}
      onClick={toggleMaximized}
      selected={maximized}
      colors={colors}
      disabled={disabled}
    />
  );
};
